import { useState } from 'react';
import styled from '@emotion/styled';
import { useConsumerT } from '../translations/landingConsumer';
import { Link } from '@mui/material';
import { track } from '@plausible-analytics/tracker';

// Palette inspirée du ticket doré de Charlie (version soft)
const GOLD_PRIMARY = '#D9A641';    // Or doux principal
const GOLD_LIGHT = '#F4E5C3';      // Crème doré
const GOLD_DARK = '#8B6914';       // Or foncé pour le texte
const CREAM = '#FFF9E6';           // Ivoire crème

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

function isValidPhone(v) {
  return /^(\+33|0)[1-9](\s?\d{2}){4}$/.test(v.replace(/[\s.-]/g, ' ').trim());
}

export default function LotteryForm({ onSubmit, onSuccessClose, onDuplicateRescan }) {
  const t = useConsumerT();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  const emailError = emailTouched && email.length > 0 && !isValidEmail(email);
  const phoneError = phoneTouched && phone.length > 0 && !isValidPhone(phone);
  const canSubmit = isValidEmail(email) && !phoneError && !submitting && rulesAccepted;

  const isPrd = process.env.REACT_APP_ENV === 'PRD';

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    if (isPrd) track('participation_clicked', { props: { marketing_consent: String(marketingAccepted) } });
    setSubmitting(true);
    try {
      const result = await onSubmit({ email, phone: phone.trim() || null, marketingAccepted });
      if (result === true) {
        if (isPrd) track('participation_saved', { props: { marketing_consent: String(marketingAccepted) } });
        setPopupOpen(true);
      } else if (result === 'duplicate') { setDuplicateOpen(true); }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {popupOpen && (
        <SuccessOverlay role="dialog" aria-modal="true" onClick={() => setPopupOpen(false)}>
          <SuccessCard onClick={e => e.stopPropagation()}>
            <SuccessStar>⭐</SuccessStar>
            <SuccessTitle>{t('bravo.lottery-success-title')}</SuccessTitle>
            <SuccessBody>{t('bravo.lottery-success')}</SuccessBody>
            <CloseBtn type="button" onClick={() => { setPopupOpen(false); onSuccessClose?.(); }}>
              {t('common.close')}
            </CloseBtn>
          </SuccessCard>
        </SuccessOverlay>
      )}

      {duplicateOpen && (
        <SuccessOverlay role="dialog" aria-modal="true" onClick={() => setDuplicateOpen(false)}>
          <SuccessCard onClick={e => e.stopPropagation()}>
            <SuccessStar>ℹ️</SuccessStar>
            <SuccessTitle>{t('bravo.lottery-duplicate-title')}</SuccessTitle>
            <SuccessBody>{t('bravo.lottery-error-duplicate')}</SuccessBody>
            <DuplicateActions>
              <CloseBtn type="button" onClick={() => { setDuplicateOpen(false); onSuccessClose?.(); }}>
                {t('bravo.restart-cta')}
              </CloseBtn>
              <RescanBtn type="button" onClick={() => { setDuplicateOpen(false); onDuplicateRescan?.(); }}>
                {t('bravo.lottery-rescan-cta')}
              </RescanBtn>
            </DuplicateActions>
          </SuccessCard>
        </SuccessOverlay>
      )}

      <Form onSubmit={handleSubmit}>
        <InstructionText>{t('bravo.lottery-email-instruction')}</InstructionText>

        <FieldLabel htmlFor="lottery-email">{t('bravo.lottery-email-label')}</FieldLabel>
        <Input
          id="lottery-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          onBlur={() => setEmailTouched(true)}
          placeholder={t('bravo.lottery-email-placeholder')}
          $filled={email.length > 0}
          $error={emailError}
        />
        {emailError && <FieldError>{t('bravo.lottery-email-error')}</FieldError>}

        <FieldLabel htmlFor="lottery-phone">{t('bravo.lottery-phone-label')}</FieldLabel>
        <Input
          id="lottery-phone"
          type="tel"
          autoComplete="tel"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          onBlur={() => setPhoneTouched(true)}
          placeholder={t('bravo.lottery-phone-placeholder')}
          $filled={phone.length > 0}
          $error={phoneError}
        />
        {phoneError && <FieldError>{t('bravo.lottery-phone-error')}</FieldError>}

        <ConsentRow>
          <ConsentCheckbox
            id="lottery-rules"
            type="checkbox"
            checked={rulesAccepted}
            onChange={e => setRulesAccepted(e.target.checked)}
          />
          <ConsentLabel htmlFor="lottery-rules">
            {t('bravo.lottery-consent-before')}
            <ConsentLink
              as={Link}
              href="/official_rules_golden_ticket.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('bravo.lottery-consent-link')}
            </ConsentLink>
            {t('bravo.lottery-consent-after')}
            <ConsentRequired> * (obligatoire)</ConsentRequired>
          </ConsentLabel>
        </ConsentRow>

        <ConsentRow>
          <ConsentCheckbox
            id="lottery-marketing"
            type="checkbox"
            checked={marketingAccepted}
            onChange={e => setMarketingAccepted(e.target.checked)}
          />
          <ConsentLabel htmlFor="lottery-marketing">
            {t('bravo.lottery-consent-marketing')}
          </ConsentLabel>
        </ConsentRow>

        <SubmitBtn
          type="submit"
          disabled={!canSubmit}
          $enabled={canSubmit}
        >
          {submitting ? <Spinner /> : (canSubmit ? t('bravo.lottery-submit-active') : t('bravo.lottery-submit-inactive'))}
        </SubmitBtn>
      </Form>
    </>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: ${p => p.theme.typography.fontFamily};
`;

const InstructionText = styled.p`
  margin: 0 0 4px;
  font-size: 13px;
  color: ${GOLD_DARK};
  line-height: 1.5;
  text-align: center;
  font-weight: 500;
`;

const FieldLabel = styled.label`
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: ${GOLD_PRIMARY};
`;

const Input = styled.input`
  padding: 12px 14px;
  border-radius: 10px;
  border: 2px solid ${p => p.$error ? p.theme.palette.error.main : p.$filled ? GOLD_PRIMARY : GOLD_LIGHT};
  font-size: 14px;
  color: ${GOLD_DARK};
  background: #FFFFFF;
  outline: none;
  box-sizing: border-box;
  font-family: ${p => p.theme.typography.fontFamily};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &::placeholder {
    color: rgba(139, 105, 20, 0.4);
  }
  &:focus-visible {
    border-color: ${GOLD_PRIMARY};
    box-shadow: 0 0 0 3px rgba(217, 166, 65, 0.15);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const FieldError = styled.span`
  font-size: 11px;
  color: ${p => p.theme.palette.error.light};
  margin-top: -6px;
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const ConsentRequired = styled.span`
  font-size: 11px;
  color: ${GOLD_DARK};
  font-weight: 400;
`;

const ConsentCheckbox = styled.input`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 1px;
  accent-color: ${GOLD_PRIMARY};
  cursor: pointer;
`;

const ConsentLabel = styled.label`
  font-size: 12px;
  color: ${GOLD_DARK};
  line-height: 1.5;
  cursor: pointer;
`;

const ConsentLink = styled.span`
  color: ${GOLD_PRIMARY};
  text-decoration: underline;
  font-weight: 700;
`;

const SubmitBtn = styled.button`
  padding: 14px 20px;
  border-radius: 999px;
  border: none;
  background: ${p => p.$enabled ? `linear-gradient(135deg, ${GOLD_PRIMARY} 0%, #C79434 100%)` : GOLD_LIGHT};
  color: ${p => p.$enabled ? '#FFFFFF' : 'rgba(139, 105, 20, 0.5)'};
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 900;
  cursor: ${p => p.$enabled ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  box-shadow: ${p => p.$enabled ? `0 4px 20px rgba(217, 166, 65, 0.4), 0 0 40px rgba(217, 166, 65, 0.2)` : 'none'};
  text-shadow: ${p => p.$enabled ? '0 1px 2px rgba(0, 0, 0, 0.2)' : 'none'};
  
  &:active {
    transform: ${p => p.$enabled ? 'scale(0.98)' : 'none'};
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const spin = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Spinner = styled.span`
  ${spin}
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.5;
  }
`;

const SuccessOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.25s ease;
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SuccessCard = styled.div`
  background: linear-gradient(135deg, ${CREAM} 0%, #FDEFC9 50%, ${GOLD_LIGHT} 100%);
  border: 3px solid ${GOLD_PRIMARY};
  border-radius: 20px;
  padding: 40px 32px;
  text-align: center;
  max-width: 340px;
  width: 100%;
  box-shadow: 
    0 8px 32px rgba(217, 166, 65, 0.3),
    0 0 60px rgba(217, 166, 65, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  animation: popIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  @keyframes popIn {
    from { transform: scale(0.85); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SuccessStar = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
  line-height: 1;
  filter: drop-shadow(0 2px 8px rgba(217, 166, 65, 0.3));
`;

const SuccessTitle = styled.div`
  font-size: 22px;
  font-weight: 900;
  color: ${GOLD_PRIMARY};
  letter-spacing: -0.5px;
  margin-bottom: 10px;
  font-family: inherit;
  text-shadow: 0 1px 2px rgba(217, 166, 65, 0.2);
`;

const SuccessBody = styled.div`
  font-size: 14px;
  color: ${GOLD_DARK};
  line-height: 1.6;
  font-family: inherit;
  margin-bottom: 24px;
  font-weight: 500;
`;

const DuplicateActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const CloseBtn = styled.button`
  padding: 12px 32px;
  border-radius: 999px;
  border: 2px solid ${GOLD_PRIMARY};
  background: linear-gradient(135deg, ${GOLD_PRIMARY} 0%, #C79434 100%);
  color: #FFFFFF;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(217, 166, 65, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;

  &:active {
    transform: scale(0.97);
  }
`;

const RescanBtn = styled.button`
  padding: 11px 32px;
  border-radius: 999px;
  border: 1.5px solid ${GOLD_PRIMARY};
  background: transparent;
  color: ${GOLD_DARK};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: background 0.2s ease;

  &:active {
    background: rgba(217, 166, 65, 0.08);
  }
`;
